/**
 * Encrypted attachments.
 *
 * Each upload is AES-256-GCM encrypted with a fresh random key on the
 * client. The key + nonce live inside the MLS-encrypted message that
 * references the upload; R2 only ever sees ciphertext.
 *
 * Blob layout: `[nonce:12][ciphertext+tag]`.
 */
import { fromBase64Url, toBase64Url, type UserId } from '@chat/protocol';
import type { ChatClient } from './api.js';

const NONCE_BYTES = 12;
const KEY_BYTES = 32;

function asBuffer(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(bytes.length));
  out.set(bytes);
  return out;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

async function importKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', asBuffer(raw), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

export interface AttachmentReference {
  readonly mediaId: string;
  readonly uploaderUserId: UserId;
  /** base64url AES-256 key. */
  readonly key: string;
  /** Decrypted size for previews / quota accounting. */
  readonly size: number;
  /** MIME type as reported by the sender (opaque to the server). */
  readonly mime: string;
  /** Original filename, if any. */
  readonly filename: string;
}

export interface AttachmentInput {
  readonly bytes: Uint8Array;
  readonly mime: string;
  readonly filename: string;
}

export async function encryptAttachment(plaintext: Uint8Array): Promise<{
  readonly rawKey: Uint8Array;
  readonly body: Uint8Array;
}> {
  const rawKey = new Uint8Array(KEY_BYTES);
  crypto.getRandomValues(rawKey);
  const nonce = new Uint8Array(NONCE_BYTES);
  crypto.getRandomValues(nonce);
  const key = await importKey(rawKey);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asBuffer(nonce) }, key, asBuffer(plaintext)),
  );
  return { rawKey, body: concat(nonce, ciphertext) };
}

export async function decryptAttachment(rawKey: Uint8Array, body: Uint8Array): Promise<Uint8Array> {
  if (body.length < NONCE_BYTES + 16) throw new Error('attachment body too short');
  const nonce = body.subarray(0, NONCE_BYTES);
  const ciphertext = body.subarray(NONCE_BYTES);
  const key = await importKey(rawKey);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: asBuffer(nonce) },
    key,
    asBuffer(ciphertext),
  );
  return new Uint8Array(plaintext);
}

export class AttachmentService {
  readonly #client: ChatClient;
  readonly #tokenProvider: () => Promise<string> | string;
  readonly #uploaderUserId: UserId;

  constructor(deps: {
    client: ChatClient;
    tokenProvider: () => Promise<string> | string;
    uploaderUserId: UserId;
  }) {
    this.#client = deps.client;
    this.#tokenProvider = deps.tokenProvider;
    this.#uploaderUserId = deps.uploaderUserId;
  }

  /**
   * Encrypt `plaintext` with a fresh key, upload the ciphertext, and return
   * a reference the caller includes inside an MLS application message.
   */
  async upload(input: AttachmentInput): Promise<AttachmentReference> {
    const { rawKey, body } = await encryptAttachment(input.bytes);
    const token = await this.#tokenProvider();
    const result = await this.#client.uploadMedia(token, body);
    return {
      mediaId: result.mediaId,
      uploaderUserId: this.#uploaderUserId,
      key: toBase64Url(rawKey),
      size: input.bytes.byteLength,
      mime: input.mime,
      filename: input.filename,
    };
  }

  /**
   * Fetch the ciphertext for a reference and decrypt it. Throws if the
   * caller's key doesn't match or the blob is tampered.
   */
  async download(reference: AttachmentReference): Promise<Uint8Array> {
    const token = await this.#tokenProvider();
    const body = await this.#client.downloadMedia(
      token,
      reference.uploaderUserId,
      reference.mediaId,
    );
    return decryptAttachment(fromBase64Url(reference.key), body);
  }
}
